/* iter55m — Beat-Map JSON Import dialog.
 *
 * Creatives paste a beat map (the format shipped in
 * /app/backend/app/scripts/dark_romance_beat_map.json) into a textarea;
 * we validate + POST it to /api/admin/starter-templates/import which
 * upserts the resulting template into app_config.starter_templates so
 * it appears immediately in the picker.
 */
import React, { useState } from 'react';
import { Loader2, Upload, FileJson, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { adminApi } from '@/lib/api';
import { toast } from 'sonner';

const PLACEHOLDER = `{
  "0": {"title": "Prologue", "pov": "Hero", "word_count": 1000, "beat": "Cold open..."},
  "1": {"title": "The Hook", "pov": "Heroine", "word_count": 3500,
        "beat": "Open in media res...",
        "alt_beats": [
          {"label": "Slow-burn variant", "beat": "Same setup but restrained..."}
        ]}
}`;

export default function BeatMapImportDialog({ open, onOpenChange, onImported }) {
  const [name,    setName]    = useState('');
  const [icon,    setIcon]    = useState('Skull');
  const [tagline, setTagline] = useState('');
  const [json,    setJson]    = useState('');
  const [busy,    setBusy]    = useState(false);
  // iter55n — Placeholder auto-detect + accept/reject swaps.
  const [suggestions,   setSuggestions]   = useState(null);
  const [swapAccept,    setSwapAccept]    = useState({});
  const [swapsResolved, setSwapsResolved] = useState(false);

  const submit = async () => {
    if (!name.trim()) { toast.error('Give the template a name.'); return; }
    let parsed;
    try {
      parsed = JSON.parse(json);
    } catch (e) {
      toast.error('Beat map is not valid JSON.');
      return;
    }
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed) ||
         Object.keys(parsed).length === 0) {
      toast.error('Beat map must be an object keyed by chapter index (0, 1, 2 …).');
      return;
    }
    // iter55n — Auto-detect placeholders BEFORE the import so ops can
    // accept / reject each swap. If no matches, skip straight to import.
    if (!swapsResolved) {
      try {
        const r = await adminApi.detectPlaceholders(parsed);
        const suggestions = r?.suggestions || [];
        if (suggestions.length > 0) {
          setSuggestions(suggestions);
          const initial = {};
          suggestions.forEach((s) => {
            s.matches.forEach((m, i) => { initial[`${s.chapter}::${i}`] = true; });
          });
          setSwapAccept(initial);
          return;
        }
      } catch {
        // If detect fails, fall through to a raw import.
      }
    }
    // Apply accepted swaps to the JSON before posting.
    let finalMap = parsed;
    if (suggestions && swapsResolved) {
      finalMap = { ...parsed };
      suggestions.forEach((s) => {
        const entry = { ...finalMap[String(s.chapter)] };
        let beat = String(entry.beat || '');
        // Apply swaps back-to-front so indices stay valid.
        const kept = s.matches
          .map((m, i) => ({ ...m, i, accept: swapAccept[`${s.chapter}::${i}`] }))
          .filter((m) => m.accept)
          .sort((a, b) => b.start - a.start);
        for (const m of kept) {
          beat = beat.slice(0, m.start) + m.replace_to + beat.slice(m.end);
        }
        entry.beat = beat;
        finalMap[String(s.chapter)] = entry;
      });
    }
    setBusy(true);
    try {
      const r = await adminApi.importBeatMap({
        beat_map: finalMap,
        name:     name.trim(),
        icon:     icon.trim() || 'Skull',
        tagline:  tagline.trim(),
      });
      toast.success(
        r.replaced
          ? `Template "${r.template.name}" updated — ${r.template.chapters.length} beats`
          : `Template "${r.template.name}" imported — ${r.template.chapters.length} beats`,
      );
      setName(''); setTagline(''); setJson('');
      setSuggestions(null); setSwapAccept({}); setSwapsResolved(false);
      onImported?.(r.template);
      onOpenChange?.(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Import failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="beat-map-import-dialog">
        <DialogHeader>
          <DialogTitle className="text-[15px] inline-flex items-center gap-2">
            <FileJson className="h-4 w-4 text-primary" /> Import beat map JSON
          </DialogTitle>
          <DialogDescription>
            Paste a chapter-by-chapter beat map. We&apos;ll auto-generate
            variable placeholders ({'{{hero_name}}'}, {'{{heroine_name}}'},
            setting, trap) and add the template to the picker for every
            creative on the platform.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-3 sm:grid-cols-3">
          <div className="sm:col-span-2">
            <Label className="text-[10.5px] uppercase tracking-wider">Template name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)}
                    placeholder="Dark Romance (26-beat)"
                    data-testid="beat-map-import-name" />
          </div>
          <div>
            <Label className="text-[10.5px] uppercase tracking-wider">Icon (lucide)</Label>
            <Input value={icon} onChange={(e) => setIcon(e.target.value)}
                    placeholder="Skull"
                    data-testid="beat-map-import-icon" />
          </div>
        </div>

        <div>
          <Label className="text-[10.5px] uppercase tracking-wider">Tagline (optional)</Label>
          <Input value={tagline} onChange={(e) => setTagline(e.target.value)}
                  placeholder="Auto-computed if left blank"
                  data-testid="beat-map-import-tagline" />
        </div>

        <div>
          <div className="flex items-center justify-between">
            <Label className="text-[10.5px] uppercase tracking-wider">Beat map JSON</Label>
            <span className="text-[10px] text-muted-foreground inline-flex items-center gap-1">
              <Sparkles className="h-2.5 w-2.5" />
              {`{ "0": {title, pov, word_count, beat, alt_beats?}, ... }`}
            </span>
          </div>
          <Textarea value={json} onChange={(e) => setJson(e.target.value)}
                    placeholder={PLACEHOLDER}
                    className="mt-1 h-64 font-mono text-[11.5px]"
                    data-testid="beat-map-import-json" />
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange?.(false)}
                  data-testid="beat-map-import-cancel">
            Cancel
          </Button>
          {suggestions && !swapsResolved ? (
            <Button onClick={() => { setSwapsResolved(true); submit(); }}
                    className="gap-1.5"
                    data-testid="beat-map-import-apply-swaps">
              <Upload className="h-3.5 w-3.5" /> Apply {Object.values(swapAccept).filter(Boolean).length} swaps and import
            </Button>
          ) : (
            <Button onClick={submit} disabled={busy || !json.trim() || !name.trim()}
                    className="gap-1.5"
                    data-testid="beat-map-import-submit">
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
              Import template
            </Button>
          )}
        </DialogFooter>
        {suggestions && (
          <div className="mt-2 rounded-md border border-amber-500/40 bg-amber-500/5 p-2 text-[11.5px]"
               data-testid="beat-map-import-suggestions">
            <div className="mb-1 font-semibold text-amber-800 dark:text-amber-300">
              Found {suggestions.reduce((n, s) => n + s.matches.length, 0)} placeholder swaps across {suggestions.length} chapter{suggestions.length === 1 ? '' : 's'}
            </div>
            <ul className="space-y-0.5">
              {suggestions.map((s) =>
                s.matches.map((m, i) => {
                  const key = `${s.chapter}::${i}`;
                  return (
                    <li key={key} className="flex items-center gap-1.5">
                      <input
                        type="checkbox"
                        checked={!!swapAccept[key]}
                        onChange={(e) => setSwapAccept((prev) => ({ ...prev, [key]: e.target.checked }))}
                        data-testid={`beat-map-import-swap-${key}`}
                      />
                      <span className="font-mono-stat text-[10px] text-muted-foreground">Ch {s.chapter}</span>
                      <span className="rounded-sm bg-muted px-1 text-[10.5px]">{m.match}</span>
                      <span className="text-[10.5px] text-muted-foreground">→</span>
                      <span className="rounded-sm bg-primary/10 px-1 text-[10.5px] text-primary">{m.replace_to}</span>
                    </li>
                  );
                }),
              )}
            </ul>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
